import { ChevronLeft } from 'lucide-react'
import { useState } from 'react'

import { BillingBreakdownHeader } from 'components/billing/BillingBreakdownHeader'
import { InvoiceProvider } from 'components/billing/InvoiceContext'
import { MemberInvoiceDetails } from 'components/billing/MemberInvoiceDetails'
import { StripeInvoiceBreakdown } from 'components/billing/StripeInvoiceBreakdown'
import { ThatchInvoiceBreakdown } from 'components/billing/ThatchInvoiceBreakdown'
import { TextButton } from 'slab'
import { formatMonthName, formatPeriodDate, type InvoiceType } from 'utils/billing'
import {
  useGetEmployersEmployerInvoicesId,
  useGetEmployersEmployerInvoicesStripeId,
} from 'utils/web_api'
import { type EmployersMemberInvoiceDetail } from 'utils/web_api.schemas'

interface BillingBreakdownProps {
  invoiceId: string
  invoiceType: InvoiceType
  onBack: () => void
}

export function BillingBreakdown ({ invoiceId, invoiceType, onBack }: BillingBreakdownProps) {
  if (invoiceType === 'stripe') {
    return <StripeBillingBreakdown invoiceId={invoiceId} onBack={onBack} />
  }
  return <ThatchBillingBreakdown invoiceId={invoiceId} onBack={onBack} />
}

function ThatchBillingBreakdown ({ invoiceId, onBack }: { invoiceId: string; onBack: () => void }) {
  const [selectedMemberInvoiceId, setSelectedMemberInvoiceId] = useState<string | null>(null)
  const { data, isLoading, isError } = useGetEmployersEmployerInvoicesId(invoiceId)

  if (isLoading) {
    return (
      <div className='flex items-center justify-center py-12'>
        <div className='text-gray-500'>Loading invoice details...</div>
      </div>
    )
  }

  if (isError || !data) {
    return (
      <div className='mt-2 mb-4 p-4 text-sm text-slate-600 border-2 border-red-500 rounded-lg shadow-md'>
        <div>
          There was an error fetching invoice details. Please reach out to{' '}
          <a href='mailto:support@thatch.com'>support@thatch.com</a> if you
          continue to experience this error.
        </div>
      </div>
    )
  }

  const invoice = data
  const monthName = formatMonthName(invoice.period_end_date)
  const periodStartFormatted = formatPeriodDate(invoice.period_start_date)
  const periodEndFormatted = formatPeriodDate(invoice.period_end_date)
  const showPlanYear = (data?.plan_years?.length ?? 0) > 1
  const enrichedMemberInvoices: EmployersMemberInvoiceDetail[] = data.member_invoices || []

  if (selectedMemberInvoiceId) {
    return (
      <InvoiceProvider invoice={invoice} enrichedMemberInvoices={enrichedMemberInvoices}>
        <MemberInvoiceDetails
          memberInvoiceId={selectedMemberInvoiceId}
          onBack={() => setSelectedMemberInvoiceId(null)}
        />
      </InvoiceProvider>
    )
  }

  return (
    <div>
      <div className='mb-4'>
        <TextButton
          color='gray'
          leftIcon={ChevronLeft}
          onClick={onBack}
        >
          Back to all bills
        </TextButton>
      </div>

      <BillingBreakdownHeader
        titleText={`${monthName} healthcare costs`}
        subheaderText={`Includes costs from ${periodStartFormatted} - ${periodEndFormatted}`}
        invoiceNumber={invoice.invoice_number}
        invoice={invoice}
        enrichedMemberInvoices={enrichedMemberInvoices}
        reportDownloadUrl={data?.latest_report?.download_url}
        showPlanYear={showPlanYear}
      />

      <ThatchInvoiceBreakdown
        invoice={invoice}
        enrichedMemberInvoices={enrichedMemberInvoices}
        onMemberClick={setSelectedMemberInvoiceId}
      />
    </div>
  )
}

function StripeBillingBreakdown ({ invoiceId, onBack }: { invoiceId: string; onBack: () => void }) {
  const { data, isLoading, isError } = useGetEmployersEmployerInvoicesStripeId(invoiceId)

  if (isLoading) {
    return (
      <div className='flex items-center justify-center py-12'>
        <div className='text-gray-500'>Loading invoice details...</div>
      </div>
    )
  }

  if (isError || !data) {
    return (
      <div className='mt-2 mb-4 p-4 text-sm text-slate-600 border-2 border-red-500 rounded-lg shadow-md'>
        <div>
          There was an error fetching invoice details. Please reach out to{' '}
          <a href='mailto:support@thatch.com'>support@thatch.com</a> if you
          continue to experience this error.
        </div>
      </div>
    )
  }

  const invoice = data
  const invoiceDate = new Date(invoice.created)
  const monthName = formatMonthName(invoice.created)
  const firstDayOfMonth = new Date(invoiceDate.getFullYear(), invoiceDate.getMonth(), 1)
  const firstDayFormatted = firstDayOfMonth.toLocaleDateString('en-US', {
    month: 'long',
    day: 'numeric',
  })

  return (
    <div>
      <div className='mb-4'>
        <TextButton
          color='gray'
          leftIcon={ChevronLeft}
          onClick={onBack}
        >
          Back to all bills
        </TextButton>
      </div>

      <BillingBreakdownHeader
        titleText={`${monthName} platform fees`}
        subheaderText={`Includes enrolled employees as of ${firstDayFormatted}`}
        invoiceNumber={invoice.invoice_number || invoice.id}
        invoice={invoice}
      />

      <StripeInvoiceBreakdown invoice={invoice} />
    </div>
  )
}

export default BillingBreakdown
